import { useState } from 'react';
import { TrendingUp, Flame, Sparkles, Target, ArrowRight, RefreshCw, Lightbulb, Star, LayoutGrid } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { cn } from './ui/utils';

type TabType = 'social' | 'creator' | 'content' | 'marketing';

interface Trend {
  id: string;
  title: string;
  description: string;
  whyItMatters: string;
  viralityScore: number;
  relevanceScore: number;
  opportunityRating: 'high' | 'medium' | 'low';
  origin: string;
  category: string;
  image: string;
}

export function TrendsPage() {
  const [activeTab, setActiveTab] = useState<TabType>('social');

  const trends: Record<TabType, Trend[]> = {
    social: [
      {
        id: 's1',
        title: 'De-influencing Movement',
        description: 'Creators openly sharing what NOT to buy, calling out overhyped products to build extreme trust with audiences.',
        whyItMatters: 'Shifts power from brands to creators. Brands must now prove efficacy rather than just buying reach.',
        viralityScore: 94,
        relevanceScore: 88,
        opportunityRating: 'high',
        origin: 'TikTok',
        category: 'Cultural Shift',
        image: 'https://images.unsplash.com/photo-1697125045982-0d703bc35e96?w=800&q=80',
      },
      {
        id: 's2',
        title: 'Quiet Luxury Aesthetic',
        description: 'Understated wealth signals. No logos, timeless quality over flashy branding. A reaction to post-pandemic excess.',
        whyItMatters: 'Requires a shift in visual storytelling from flashy and loud to high-quality, craft-focused narratives.',
        viralityScore: 87,
        relevanceScore: 76,
        opportunityRating: 'medium',
        origin: 'Instagram',
        category: 'Visual Trend',
        image: 'https://images.unsplash.com/photo-1758297679736-2e6ff92d2021?w=800&q=80',
      },
      {
        id: 's3',
        title: 'AI UGC Revolution',
        description: 'AI-generated user content, synthetic creators, and AI-enhanced testimonials gaining mainstream traction.',
        whyItMatters: 'Massively lowers production costs and allows for hyper-personalized creative testing at scale.',
        viralityScore: 78,
        relevanceScore: 85,
        opportunityRating: 'high',
        origin: 'Cross-platform',
        category: 'Tech Trend',
        image: 'https://images.unsplash.com/photo-1770233621425-5d9ee7a0a700?w=800&q=80',
      },
      {
        id: 's4',
        title: 'The Solo-Entrepreneur Peak',
        description: 'Content creators building their own physical product lines rather than just accepting brand deals.',
        whyItMatters: 'Brands are now competing with their own ambassadors for consumer wallet share. Integration must be deeper.',
        viralityScore: 82,
        relevanceScore: 91,
        opportunityRating: 'high',
        origin: 'YouTube',
        category: 'Economy',
        image: 'https://images.unsplash.com/photo-1765372860684-69f0e2524694?w=800&q=80',
      }
    ],
    creator: [
      {
        id: 'c1',
        title: 'Micro-Expert Archetype',
        description: 'Highly specialized creators (e.g., cosmetic chemists, tax lawyers) replacing general influencers.',
        whyItMatters: 'Higher conversion rates due to perceived authority. Niche reach is more valuable than mass reach.',
        viralityScore: 85,
        relevanceScore: 89,
        opportunityRating: 'high',
        origin: 'TikTok/YouTube',
        category: 'Expertise',
        image: 'https://images.unsplash.com/photo-1768295984707-64ab37e539dd?w=800&q=80',
      },
      {
        id: 'c2',
        title: 'Comedy-First Brand Content',
        description: 'Creators using sketch comedy and parody to deliver brand messages as entertainment first.',
        whyItMatters: 'High engagement and shareability. Reduces "ad fatigue" by making the ad the most fun part of the feed.',
        viralityScore: 88,
        relevanceScore: 81,
        opportunityRating: 'medium',
        origin: 'Instagram',
        category: 'Entertainment',
        image: 'https://images.unsplash.com/photo-1762537132884-cc6bbde0667a?w=800&q=80',
      }
    ],
    content: [
      {
        id: 'con1',
        title: 'Lo-Fi Authenticity Wins',
        description: 'Raw, unpolished smartphone footage outperforming high-budget studio production across all metrics.',
        whyItMatters: 'Lower production barriers. Brands can produce 10x more content for the same budget by going lo-fi.',
        viralityScore: 95,
        relevanceScore: 94,
        opportunityRating: 'high',
        origin: 'TikTok',
        category: 'Visual Style',
        image: 'https://images.unsplash.com/photo-1759393851741-674ee71fb498?w=800&q=80',
      },
      {
        id: 'con2',
        title: 'POV Storytelling',
        description: 'First-person perspective camera work that puts the viewer directly in the creators shoes.',
        whyItMatters: 'Creates an intimate connection. Perfect for product demos and lifestyle integration.',
        viralityScore: 90,
        relevanceScore: 92,
        opportunityRating: 'high',
        origin: 'TikTok',
        category: 'Format',
        image: 'https://images.unsplash.com/photo-1609357912334-e96886c0212b?w=800&q=80',
      }
    ],
    marketing: [
      {
        id: 'm1',
        title: 'Creator Whitelisting',
        description: 'Running brand ads directly through creator accounts rather than the brand account.',
        whyItMatters: 'Ads feel like organic recommendations. 4x better click-through rates than standard brand ads.',
        viralityScore: 84,
        relevanceScore: 95,
        opportunityRating: 'high',
        origin: 'Meta/TikTok',
        category: 'Ad Strategy',
        image: 'https://images.unsplash.com/photo-1759393851741-674ee71fb498?w=800&q=80',
      }
    ]
  };

  const getRatingColor = (rating: string) => {
    switch (rating) {
      case 'high': return 'bg-green-100 text-green-700 border-green-200';
      case 'medium': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] pb-20">
      <div className="max-w-[1400px] mx-auto p-8">
        
        {/* Header / Nav Area as per Wireframe */}
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 mb-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-[#2e024c] rounded-xl">
                  <TrendingUp className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-2xl font-bold text-[#101828]">Trend Explorer</h1>
              </div>
              <p className="text-gray-600 text-sm">Real-time signals from the creator economy, cultural shifts, and marketing tech.</p>
            </div>

            <div className="flex bg-gray-50 p-1.5 rounded-2xl border border-gray-200">
              {(['social', 'creator', 'content', 'marketing'] as TabType[]).map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={cn(
                    "px-6 py-2 rounded-xl text-sm font-medium transition-all",
                    activeTab === tab 
                      ? "bg-white text-[#be43ff] shadow-sm" 
                      : "text-gray-500 hover:text-gray-900"
                  )}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Grid Area as per Wireframe */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {trends[activeTab].map((trend) => (
            <div 
              key={trend.id} 
              className="flex flex-col sm:flex-row bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 group min-h-[280px]"
            >
              {/* Image Block */}
              <div className="sm:w-[220px] h-[200px] sm:h-full relative overflow-hidden flex-shrink-0">
                <ImageWithFallback 
                  src={trend.image} 
                  alt={trend.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-white/90 text-gray-900 border-0 backdrop-blur-sm text-[10px] font-bold uppercase tracking-wider">
                    {trend.category}
                  </Badge>
                </div>
              </div>

              {/* Trend Content Block */}
              <div className="flex-1 p-6 flex flex-col">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-[18px] font-bold text-[#101828] group-hover:text-[#be43ff] transition-colors">
                      {trend.title}
                    </h3>
                    <Badge className={cn("border text-[10px]", getRatingColor(trend.opportunityRating))}>
                      {trend.opportunityRating.toUpperCase()} OP
                    </Badge>
                  </div>
                  <p className="text-[14px] text-gray-600 leading-relaxed mb-6">
                    {trend.description}
                  </p>
                </div>

                <div className="flex items-center gap-4 mt-auto pt-4 border-t border-gray-50">
                   <div className="flex items-center gap-1.5">
                      <Flame className="w-3.5 h-3.5 text-orange-500" />
                      <span className="text-[12px] font-semibold text-gray-900">{trend.viralityScore}</span>
                      <span className="text-[10px] text-gray-500 font-medium">Viral</span>
                   </div>
                   <div className="flex items-center gap-1.5">
                      <Target className="w-3.5 h-3.5 text-[#be43ff]" />
                      <span className="text-[12px] font-semibold text-gray-900">{trend.relevanceScore}</span>
                      <span className="text-[10px] text-gray-500 font-medium">Match</span>
                   </div>
                   <div className="ml-auto text-[10px] text-gray-400 font-medium italic">
                      via {trend.origin}
                   </div>
                </div>
              </div>

              {/* Why it Matters Block */}
              <div className="sm:w-[220px] bg-[#f3ecff] p-6 flex flex-col justify-center border-l border-gray-100">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-6 h-6 rounded-lg bg-[#e5d4ff] flex items-center justify-center">
                    <Lightbulb className="w-3.5 h-3.5 text-[#be43ff]" />
                  </div>
                  <span className="text-[12px] font-bold text-[#2e024c] uppercase tracking-tight">Strategy</span>
                </div>
                <p className="text-[13px] text-[#2e024c]/80 leading-snug font-medium italic">
                   "{trend.whyItMatters}"
                </p>
                <Button className="mt-6 w-full h-9 bg-[#2e024c] text-white rounded-xl text-xs gap-2 hover:bg-[#3a0d5e] shadow-md shadow-purple-200 transition-all">
                  Apply <ArrowRight className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Insights Footer */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-gradient-to-br from-[#101828] to-[#1d2939] border-0 text-white p-6 rounded-[32px]">
            <div className="flex items-center gap-3 mb-4">
              <Star className="w-5 h-5 text-yellow-400" />
              <h4 className="font-bold">AI Prediction</h4>
            </div>
            <p className="text-sm text-gray-400 mb-4">"We expect Lo-Fi formats to dominate Q3 as consumer skepticism towards AI-generated content rises."</p>
            <div className="flex items-center gap-2 text-xs text-yellow-400 font-medium">
              <Sparkles className="w-3 h-3" /> 92% Confidence Score
            </div>
          </Card>
          
          <Card className="bg-white border border-gray-100 p-6 rounded-[32px] shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-50 rounded-xl">
                <RefreshCw className="w-5 h-5 text-orange-600" />
              </div>
              <h4 className="font-bold text-gray-900">Velocity Signal</h4>
            </div>
            <p className="text-sm text-gray-500 mb-4">Quiet Luxury mentions are cooling down (-12% WoW), while "Everyday Systems" is surging (+45%).</p>
            <Badge className="bg-orange-100 text-orange-700 border-0">High Velocity</Badge>
          </Card>

          <Card className="bg-white border border-gray-100 p-6 rounded-[32px] shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-50 rounded-xl">
                <LayoutGrid className="w-5 h-5 text-blue-600" />
              </div>
              <h4 className="font-bold text-gray-900">Format Guide</h4>
            </div>
            <p className="text-sm text-gray-500 mb-4">POV-style videos currently have the highest completion rate (82%) across all social platforms.</p>
            <Button variant="link" className="p-0 text-blue-600 text-xs h-auto font-bold">Download PDF Guide →</Button>
          </Card>
        </div>
      </div>
    </div>
  );
}